import { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Switch,
  FormControlLabel,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Tooltip,
  useTheme,
  useMediaQuery,
  Collapse,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  CalendarMonth as CalendarIcon,
  Payment as PaymentIcon,
  EventBusy as EventBusyIcon,
  Logout as LogoutIcon,
  People as PeopleIcon,
  ContentCut as ServiceIcon,
  Settings as SettingsIcon,
  ExpandLess,
  ExpandMore,
  SupportAgent as SupportAgentIcon,
  Assessment as AssessmentIcon,
} from '@mui/icons-material';
import { authService } from '../services/authService';
import usersService from '../services/usersService';
import * as supportService from '../services/supportService';
import { isDevEnvironment } from '../utils/environment';
import ThemeSelector from './ThemeSelector';
import NotificationCenter from './NotificationCenter';
import NotificationDetailModal from './NotificationDetailModal';
import NotificationErrorBoundary from './NotificationErrorBoundary';
import NotificationToast from './NotificationToast';
import { useNotificationToast } from '../hooks/useNotificationToast';
import { useThemeCustom } from '../hooks/useThemeCustom';
import { trackPageView, trackEvent } from '../utils/tracking';
import { isDarkColor, isVeryDarkColor } from '../utils/colorUtils';
import { getIconComponent } from '../utils/iconUtils';

const drawerWidth = 260;

export default function Layout() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [autoOpenNotifications, setAutoOpenNotifications] = useState(false);
  const [deepLinkNotificationId, setDeepLinkNotificationId] = useState(null);
  const [emailNotificationsEnabled, setEmailNotificationsEnabled] = useState(true);
  const [loadingEmailPref, setLoadingEmailPref] = useState(false);
  const [hasSupportTickets, setHasSupportTickets] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const userInfo = authService.getUserInfo();
  
  // Hook do tema customizado
  const { getLabel, getIcon, getColor } = useThemeCustom();
  
  // Obter cor do drawer e determinar se é escura
  const drawerBackgroundColor = getColor('backgroundDrawer');
  const isDrawerDark = isDarkColor(drawerBackgroundColor);

  // Obter cor primária e determinar se é muito escura (para gradientes adaptativos)
  const primaryColor = getColor('primary');
  const isPrimaryVeryDark = isVeryDarkColor(primaryColor);

  // Hook de toast notifications
  const { activeToasts, enqueue: enqueueToast, dismiss: dismissToast, pauseTimer, resumeTimer } = useNotificationToast();

  // Load email notification preference
  useEffect(() => {
    const loadPreferences = async () => {
      try {
        const prefs = await usersService.getNotificationPreferences();
        setEmailNotificationsEnabled(prefs.emailNotificationsEnabled);
      } catch (error) {
        console.warn('[Layout] Could not load notification preferences:', error);
      }
    };
    loadPreferences();
  }, []);

  // Load support tickets summary to check if user has tickets
  useEffect(() => {
    const loadSupportTicketsSummary = async () => {
      try {
        const summary = await supportService.getTicketsStats();
        // Se tem qualquer tipo de ticket (aberto, em progresso, resolvido ou fechado), exibir o menu
        const hasTickets = (summary.openCount || 0) + (summary.inProgressCount || 0) + 
                          (summary.resolvedCount || 0) + (summary.closedCount || 0) > 0;
        setHasSupportTickets(hasTickets);
      } catch (error) {
        console.warn('[Layout] Could not load support tickets summary:', error);
        // Em caso de erro, não exibir o menu (melhor ser restritivo)
        setHasSupportTickets(false);
      }
    };
    loadSupportTicketsSummary();
  }, []);

  // Handle email notifications toggle
  const handleEmailNotificationsToggle = useCallback(async (event) => {
    const newValue = event.target.checked;
    setLoadingEmailPref(true);
    try {
      await usersService.updateNotificationPreferences(newValue);
      setEmailNotificationsEnabled(newValue);
      trackEvent('email_notifications_toggle', { enabled: newValue });
    } catch (error) {
      console.error('[Layout] Failed to update notification preferences:', error);
      // Revert on error
    } finally {
      setLoadingEmailPref(false);
    }
  }, []);

  // Check for notification deep-link querystring (from email click)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('openNotifications') === 'true') {
      setAutoOpenNotifications(true);
      // Clean up URL without reloading
      const cleanUrl = location.pathname;
      window.history.replaceState(null, '', cleanUrl);
    }
  }, [location]);

  // Check for #notification={id} hash fragment (deep link from push/email)
  useEffect(() => {
    const hash = window.location.hash;
    const match = hash.match(/^#notification=(.+)$/);
    if (match) {
      setDeepLinkNotificationId(match[1]);
      // Clean up hash without reloading
      window.history.replaceState(null, '', location.pathname + location.search);
    }
  }, [location]);

  // Callback when notifications auto-opened
  const handleNotificationsAutoOpened = () => {
    setAutoOpenNotifications(false);
  };

  // Track page views on navigation
  useEffect(() => {
    const pageName = location.pathname.replace(/^\//, '') || 'dashboard';
    trackPageView(pageName);
  }, [location.pathname]);

  // Menu items dinâmicos baseados no tema
  const menuItems = [
    {
      key: 'dashboard',
      text: getLabel('dashboard'),
      icon: getIconComponent(getIcon('dashboard').name),
      path: '/dashboard',
      description: getLabel('dashboardDescription'),
      examples: getLabel('dashboardExamples')
    },
    {
      key: 'appointments',
      text: getLabel('appointments'),
      icon: getIconComponent(getIcon('appointments').name),
      path: '/appointments',
      description: getLabel('appointmentsDescription'),
      examples: getLabel('appointmentsExamples')
    },
    {
      key: 'schedules',
      text: getLabel('schedules'),
      icon: getIconComponent(getIcon('schedules').name),
      path: '/schedules',
      description: getLabel('schedulesDescription'),
      examples: getLabel('schedulesExamples')
    },
    {
      key: 'places',
      text: getLabel('places'),
      icon: getIconComponent(getIcon('places').name),
      path: '/places',
      description: getLabel('placesDescription'),
      examples: getLabel('placesExamples')
    },
    {
      key: 'services',
      text: getLabel('services'),
      icon: getIconComponent(getIcon('services').name),
      path: '/services',
      description: getLabel('servicesDescription'),
      examples: getLabel('servicesExamples')
    },
    {
      key: 'professionals',
      text: getLabel('professionals'),
      icon: getIconComponent(getIcon('professionals').name),
      path: '/professionals',
      description: getLabel('professionalsDescription'),
      examples: getLabel('professionalsExamples')
    },
    {
      key: 'absences',
      text: getLabel('absences'),
      icon: getIconComponent(getIcon('absences').name),
      path: '/absences',
      description: getLabel('absencesDescription'),
      examples: getLabel('absencesExamples')
    },
    {
      key: 'persons',
      text: getLabel('persons'),
      icon: getIconComponent(getIcon('persons').name),
      path: '/persons',
      description: getLabel('personsDescription'),
      examples: getLabel('personsExamples')
    },
    {
      key: 'pages',
      text: getLabel('pages') || 'Páginas Link-in-Bio',
      icon: getIconComponent(getIcon('pages')?.name || 'PageIcon'),
      path: '/pages',
      description: getLabel('pagesDescription') || 'Gerencie suas páginas personalizadas',
      examples: getLabel('pagesExamples')
    },
    {
      key: 'reports',
      text: 'Relatórios',
      icon: <AssessmentIcon />,
      path: '/reports',
      description: 'Relatórios e métricas do negócio',
      examples: 'Faturamento, agendamentos, clientes'
    },
    ...(hasSupportTickets ? [{
      key: 'supportTickets',
      text: 'Tickets de Suporte',
      icon: <SupportAgentIcon />,
      path: '/support-tickets',
      description: 'Gerencie tickets de suporte dos usuários',
      examples: 'Responder tickets, atualizar status'
    }] : []),
    ...(isDevEnvironment() ? [{
      key: 'apiHealth',
      text: 'API Health Check',
      icon: <AssessmentIcon sx={{ color: '#ff9800' }} />,
      path: '/api-health',
      description: 'Teste E2E completo das APIs (somente DEV)',
      examples: 'Testar todas as APIs, validar contratos'
    }] : []),
  ];

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleLogout = () => {
    trackEvent('admin_logout', {
      user_id: userInfo?.sub || 'unknown'
    });
    authService.removeToken();
    authService.redirectToLogin();
  };

  const handleMenuClick = (path) => {
    trackEvent('menu_navigation', {
      destination: path,
      from: location.pathname
    });
    navigate(path);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: drawerBackgroundColor }}>
      {/* Header do Menu */}
      <Box sx={{
        p: { xs: 3, md: 2 },
        background: theme => isPrimaryVeryDark
          ? `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`
          : `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
        color: 'white',
        position: 'relative',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
      }}>
        <Typography variant="h5" fontWeight="bold" sx={{ fontSize: { xs: '1.25rem', md: '1.1rem' } }}>
          {getLabel('appTitle')}
        </Typography>
        <Typography variant="caption" sx={{ opacity: 0.9, fontSize: { xs: '0.75rem', md: '0.7rem' } }}>
          {getLabel('appSubtitle')}
        </Typography>
        {/* NotificationCenter no canto superior direito (desktop only) */}
        <Box
          sx={{
            position: 'absolute',
            top: { xs: 16, md: 12 },
            right: { xs: 16, md: 12 },
            display: { xs: 'none', md: 'block' },
          }}
        >
          <NotificationErrorBoundary>
            <NotificationCenter
              autoOpen={!isMobile && autoOpenNotifications}
              onAutoOpened={handleNotificationsAutoOpened}
              onToast={!isMobile ? enqueueToast : undefined}
            />
          </NotificationErrorBoundary>
        </Box>
      </Box>

      <Divider sx={isDrawerDark ? { borderColor: 'rgba(255,255,255,0.12)' } : {}} />

      {/* Informações do Usuário com Engrenagem de Configuração */}
      <Box>
        <ListItem
          disablePadding
          secondaryAction={
            <IconButton
              edge="end"
              onClick={() => setSettingsOpen(!settingsOpen)}
              size="small"
              sx={isDrawerDark ? { color: 'rgba(255,255,255,0.7)' } : {}}
            >
              {settingsOpen ? <ExpandLess /> : <SettingsIcon />}
            </IconButton>
          }
        >
          <ListItemButton sx={{ 
            py: { xs: 2, md: 1 }, 
            px: { xs: 2, md: 1.25 },
            color: isDrawerDark ? 'rgba(255,255,255,0.7)' : 'inherit',
          }}>
            <Avatar sx={{
              bgcolor: 'primary.main',
              mr: { xs: 2, md: 1 },
              width: { xs: 40, md: 28 },
              height: { xs: 40, md: 28 },
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            }}>
              {userInfo?.name?.[0]?.toUpperCase() || 'U'}
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="subtitle2" noWrap sx={{ 
                fontSize: { xs: '0.875rem', md: '0.75rem' },
                color: isDrawerDark ? '#FFFFFF' : 'inherit',
              }}>
                {userInfo?.name || 'Usuário'}
              </Typography>
              <Typography variant="caption" noWrap sx={{ 
                fontSize: { xs: '0.75rem', md: '0.65rem' },
                color: isDrawerDark ? 'rgba(255,255,255,0.7)' : 'text.secondary',
              }}>
                {userInfo?.email || ''}
              </Typography>
            </Box>
          </ListItemButton>
        </ListItem>

        {/* Seletor de Tema (Colapsável) */}
        <Collapse in={settingsOpen} timeout="auto" unmountOnExit>
          <Box sx={{ 
            px: 2, 
            pb: 2, 
            pt: 1, 
            bgcolor: isDrawerDark ? 'rgba(255,255,255,0.08)' : 'action.hover',
          }}>
            <Typography variant="caption" sx={{ 
              mb: 1, 
              display: 'block',
              color: isDrawerDark ? 'rgba(255,255,255,0.7)' : 'text.secondary',
            }}>
              Tema da Interface
            </Typography>
            <ThemeSelector />

            {/* Toggle para Notificações por Email */}
            <Divider sx={{ my: 1.5, borderColor: isDrawerDark ? 'rgba(255,255,255,0.12)' : 'divider' }} />
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 0.5 }}>
              <Typography variant="caption" sx={{ color: isDrawerDark ? 'rgba(255,255,255,0.7)' : 'text.secondary' }}>
                📧 Notificações por Email
              </Typography>
              <Switch
                size="small"
                checked={emailNotificationsEnabled}
                onChange={handleEmailNotificationsToggle}
                disabled={loadingEmailPref}
              />
            </Box>

            {/* Link para Termos de Uso */}
            <Divider sx={{ my: 1.5, borderColor: isDrawerDark ? 'rgba(255,255,255,0.12)' : 'divider' }} />
            <ListItemButton
              onClick={() => window.open('/termos-de-uso', '_blank')}
              sx={{
                mt: 1,
                py: 1,
                px: 1,
                borderRadius: 1,
                fontSize: '0.875rem',
                color: isDrawerDark ? 'rgba(255,255,255,0.7)' : 'inherit',
                '&:hover': {
                  bgcolor: isDrawerDark ? 'rgba(255,255,255,0.08)' : 'primary.light',
                  color: isDrawerDark ? '#FFFFFF' : 'primary.main',
                }
              }}
            >
              <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                📋 Termos de Uso & LGPD
              </Typography>
            </ListItemButton>
          </Box>
        </Collapse>
      </Box>

      <Divider sx={isDrawerDark ? { borderColor: 'rgba(255,255,255,0.12)' } : {}} />

      {/* Label de Seção do Menu */}
      <Typography
        variant="overline"
        sx={{
          fontSize: '0.6rem',
          letterSpacing: '0.1em',
          color: isDrawerDark ? 'rgba(255,255,255,0.5)' : 'text.secondary',
          px: 2,
          pt: 1.5,
          pb: 0.5,
          display: 'block',
        }}
      >
        MENU
      </Typography>

      {/* Menu Items */}
      <List sx={{ flex: 1, py: { xs: 2, md: 1.5 }, color: isDrawerDark ? 'rgba(255,255,255,0.7)' : 'inherit' }}>
        {menuItems.map((item) => (
          <Tooltip
            key={item.path}
            title={
              <Box>
                <Typography variant="body2" fontWeight="bold">
                  {item.description}
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.8 }}>
                  {item.examples}
                </Typography>
              </Box>
            }
            placement="right"
            arrow
          >
            <ListItem disablePadding sx={{ mb: { xs: 0.5, md: 0.25 } }}>
              <ListItemButton
                selected={location.pathname === item.path}
                onClick={() => handleMenuClick(item.path)}
                sx={{
                  mx: { xs: 1, md: 0.5 },
                  py: { xs: 1.25, md: 0.875 },
                  borderRadius: { xs: 2, md: 1.5 },
                  borderLeft: '4px solid transparent',
                  transition: 'all 0.2s ease-in-out',
                  color: isDrawerDark ? 'rgba(255,255,255,0.7)' : 'inherit',
                  '&.Mui-selected': {
                    borderLeft: '4px solid',
                    borderLeftColor: theme => theme.palette.secondary.main,
                    background: theme => `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                    color: 'white',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                    '&:hover': {
                      background: theme => `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                    },
                    '& .MuiListItemIcon-root': {
                      color: 'white',
                    },
                  },
                  '&:hover': {
                    bgcolor: isDrawerDark ? 'rgba(255,255,255,0.08)' : 'action.hover',
                    color: isDrawerDark ? '#FFFFFF' : 'inherit',
                    transform: 'translateX(2px)',
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    color: location.pathname === item.path ? 'white' : isDrawerDark ? 'rgba(255,255,255,0.7)' : 'text.secondary',
                    minWidth: { xs: 40, md: 32 },
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.text}
                  primaryTypographyProps={{
                    fontWeight: location.pathname === item.path ? 600 : 400,
                    fontSize: { xs: '0.875rem', md: '0.75rem' },
                  }}
                />
              </ListItemButton>
            </ListItem>
          </Tooltip>
        ))}
      </List>

      <Divider sx={isDrawerDark ? { borderColor: 'rgba(255,255,255,0.12)' } : {}} />

      {/* Logout Button */}
      <List sx={{ color: isDrawerDark ? 'rgba(255,255,255,0.7)' : 'inherit' }}>
        <ListItem disablePadding>
          <ListItemButton 
            onClick={handleLogout} 
            sx={{ 
              mx: 1, 
              borderRadius: 2,
              color: isDrawerDark ? 'rgba(255,255,255,0.7)' : 'inherit',
              '&:hover': {
                bgcolor: isDrawerDark ? 'rgba(255,255,255,0.08)' : 'action.hover',
                color: isDrawerDark ? '#FFFFFF' : 'inherit',
              },
            }}
          >
            <ListItemIcon sx={{ color: isDrawerDark ? 'rgba(255,255,255,0.7)' : 'inherit' }}>
              {getIconComponent(getIcon('logout').name)}
            </ListItemIcon>
            <ListItemText primary={getLabel('logout')} />
          </ListItemButton>
        </ListItem>
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* AppBar (Mobile) */}
      {isMobile && (
        <AppBar
          position="fixed"
          sx={{
            width: '100%',
            zIndex: (theme) => theme.zIndex.drawer + 1,
            background: theme => isPrimaryVeryDark
              ? `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`
              : `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          }}
        >
          <Toolbar>
            <IconButton
              color="inherit"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
            <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
              {getLabel('appTitle')}
            </Typography>
            <NotificationErrorBoundary>
              <NotificationCenter
                autoOpen={isMobile && autoOpenNotifications}
                onAutoOpened={handleNotificationsAutoOpened}
                onToast={isMobile ? enqueueToast : undefined}
              />
            </NotificationErrorBoundary>
          </Toolbar>
        </AppBar>
      )}

      {/* Drawer (Menu Lateral) */}
      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
      >
        {/* Mobile Drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              bgcolor: drawerBackgroundColor,
            },
          }}
        >
          {drawer}
        </Drawer>

        {/* Desktop Drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              bgcolor: drawerBackgroundColor,
              borderRight: '1px solid',
              borderColor: isDrawerDark ? 'rgba(255,255,255,0.12)' : 'divider',
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { md: `calc(100% - ${drawerWidth}px)` },
          mt: { xs: 8, md: 0 }, // Margem top para mobile (AppBar)
          bgcolor: 'background.default',
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Conteúdo das páginas */}
        <Box sx={{ p: { xs: 3, md: 2 }, flexGrow: 1 }}>
          <Outlet />
        </Box>
      </Box>

      {/* Deep link notification modal */}
      <NotificationDetailModal
        open={!!deepLinkNotificationId}
        onClose={() => setDeepLinkNotificationId(null)}
        notificationId={deepLinkNotificationId}
      />

      {/* Toast notifications */}
      <NotificationToast
        toasts={activeToasts}
        onDismiss={dismissToast}
        onPauseTimer={pauseTimer}
        onResumeTimer={resumeTimer}
      />
    </Box>
  );
}

Layout.propTypes = {
  // Layout não recebe props, mas incluído para consistência
};
